// ─── Hotel Property Management System (PMS) French Translation Dictionary ─────
// Comprehensive native French & Kinyarwanda hospitality terminology for Fuko Hotel PMS:
// - Front Desk, Executive Dashboard, Room Rack, Floor Plans & Matrix
// - Reservations, Visual 14-Day Tape Chart, Bookings Table
// - Walk-In Check-In, Check-Out, Key Card allocation, Room Swaps
// - Guest Folios, Extra Charges (F&B, Laundry, Minibar), Settle & Checkout
// - Housekeeping Board, Room Turnover, Clean/Dirty/Inspected workflows
// - Room Categories, Amenities, Nightly Rate Plans, Bulk Floor Generator
// - Night Audit, End-of-Day Financial Reconciliation, ADR, RevPAR, Occupancy

export const hotelFallbacks: Record<string, Record<string, string>> = {
  fr: {
    // ── Navigation & Page Headers ──
    'Hotel Management': 'Gestion Hôtelière',
    'Hotel PMS': 'PMS Hôtelier',
    'Hotel Overview & Analytics': 'Aperçu de l\'Hôtel & Analytique',
    'Hotel Overview & Executive Command': 'Aperçu de l\'Hôtel & Pilotage Exécutif',
    'Live Room Rack & Floor Matrix': 'Grille des Chambres & Plan d\'Étage',
    'Front Desk & Reservations': 'Réception & Réservations',
    'Guest Folios & Billing Suite': 'Folios Clients & Facturation',
    'Housekeeping & Turnover Operations': 'Gouvernance & Entretien Ménager',
    'Room Categories & Pricing Plans': 'Catégories de Chambres & Tarification',
    'Financial Audit & Hospitality Reports': 'Audit Financier & Rapports Hôteliers',
    'Guest Directory & CRM': 'Répertoire des Clients & CRM',

    // Navigation Menu Labels
    'Live Room Rack & Floors': 'Grille des Chambres & Étages',
    'Room Rack & Live Matrix': 'Grille des Chambres & Étages',
    'Front Desk & Bookings': 'Réception & Réservations',
    'Housekeeping & Turnover': 'Gouvernance & Nettoyage',
    'Guest Folios & Billing': 'Folios & Facturation Clients',
    'Audit & Financial Reports': 'Audit & Rapports Financiers',
    'Sales & Revenue Reports': 'Rapports des Ventes & Revenus',
    'Room Rack & Inventory': 'Grille des Chambres & Inventaire',
    'Reservations & Tape Chart': 'Réservations & Planning Visuel',
    'Guest Billing & Folios': 'Facturation & Folios Clients',
    'Audit & Reports': 'Audit & Rapports',
    'Guest Directory': 'Répertoire des Clients',
    'Hotel Expenses': 'Dépenses Hôtelières',

    // Subtitles
    'High-level operational overview, live occupancy pace, and property performance metrics':
      'Vue d\'ensemble opérationnelle, rythme d\'occupation en direct et indicateurs de performance',
    'High-level operational overview, daily arrivals pace, and property performance metrics':
      'Vue d\'ensemble opérationnelle, arrivées prévues et indicateurs de performance',
    'Real-time room status, floor allocations, occupancy grid, and quick turnovers':
      'Statut des chambres en temps réel, répartition par étage, grille d\'occupation et entretien rapide',
    'Live arrivals, departures, in-house guests, and 14-day visual tape chart':
      'Arrivées en direct, départs, clients en séjour et planning visuel sur 14 jours',
    'Guest folios, line charges, tax computations, and payment settlement':
      'Comptes clients (folios), prestations facturées, taxes et encaissement des règlements',
    'Turnover tasks, cleanliness status tracking, and housekeeping staff assignments':
      'Planning des départs/recouches, suivi de la propreté et affectation des gouvernantes',
    'Room types, base pricing, capacities, and amenity configurations':
      'Types de chambres, tarifs de base, capacités d\'accueil et équipements',
    'Revenue analytics, daily night audit logs, ADR, RevPAR, and financial reconciliation':
      'Chiffre d\'affaires hébergement, journal d\'audit de nuit, prix moyen (ADR), RevPAR et clôture',
    'Guest relationship records, VIP tiers, contact directories, and stay histories':
      'Fiches clients, statuts VIP, coordonnées de contact et historique des séjours',

    // ── Primary Actions ──
    'Refresh': 'Actualiser',
    'Night Audit': 'Audit de Nuit',
    'Run Night Audit': 'Lancer l\'Audit de Nuit',
    'Run Daily Night Audit': 'Exécuter l\'Audit de Nuit Journalier',
    'Walk-In Check-In': 'Arrivée Directe (Walk-In)',
    'New Booking': 'Nouvelle Réservation',
    '+ New Booking': '+ Nouvelle Réservation',
    'Add Room': 'Ajouter une Chambre',
    '+ Add Room': '+ Ajouter une Chambre',
    'Add Single Room': 'Ajouter une Chambre Individuelle',
    '+ Add Single Room': '+ Ajouter une Chambre Individuelle',
    'New Room Type': 'Nouveau Type de Chambre',
    'Bulk Add Rooms': 'Génération Groupée de Chambres',
    '⚡ Bulk Add Rooms': '⚡ Génération Groupée de Chambres',
    'Bulk Floor & Room Setup': 'Configuration Groupée d\'Étage & Chambres',
    '⚡ Bulk Floor & Room Setup': '⚡ Configuration Groupée d\'Étage & Chambres',
    '⚡ Bulk Floor & Room Generator': '⚡ Générateur Groupé d\'Étage & Chambres',
    'Launch Bulk Room Generator': 'Ouvrir le Générateur de Chambres',
    'Export Audit Report': 'Exporter le Rapport d\'Audit',
    'Export CSV': 'Exporter en CSV',
    'Export Excel': 'Exporter en Excel',
    'View Folio': 'Voir le Folio',
    'Open Folio': 'Consulter le Folio',
    'Print Folio': 'Imprimer la Facture Folio',
    'Print Receipt': 'Imprimer le Reçu',
    'Check-In Guest': 'Enregistrer l\'Arrivée (Check-In)',
    'Check-Out Guest': 'Enregistrer le Départ (Check-Out)',
    'Settle & Check-Out': 'Régler & Clôturer le Départ',
    'Cancel Reservation': 'Annuler la Réservation',
    'Assign Housekeeper': 'Assigner une Femme de Chambre',
    'Mark Clean': 'Marquer Propre',
    'Mark Inspected': 'Marquer Inspectée',
    'Mark Dirty': 'Marquer à Nettoyer',
    'Post Extra Charge': 'Ajouter une Prestation',
    'Record Payment': 'Enregistrer un Paiement',
    'Charge to Room': 'Facturer sur la Chambre',

    // ── KPI Cards & Metrics ──
    'Occupancy Rate': 'Taux d\'Occupation',
    'Average Daily Rate': 'Prix Moyen Journalier (ADR)',
    'Average Daily Rate (ADR)': 'Prix Moyen Chambre (ADR)',
    'RevPAR': 'RevPAR (Revenu par Chambre Disponible)',
    'RevPAR (Revenue per Available Room)': 'RevPAR (Revenu par Chambre Disponible)',
    'In-House Guests': 'Clients en Séjour',
    'Total In-House Guests': 'Total des Clients Actuellement en Séjour',
    'Total Rooms': 'Total des Chambres',
    'Available Rooms': 'Chambres Disponibles',
    'Occupied Rooms': 'Chambres Occupées',
    'Reserved Rooms': 'Chambres Réservées',
    'Cleaning Rooms': 'Chambres en Nettoyage',
    'Maintenance Rooms': 'Chambres en Maintenance',
    'Out of Order': 'Hors Service',
    'Arrivals Today': 'Arrivées Aujourd\'hui',
    'Departures Today': 'Départs Aujourd\'hui',
    'Pending Turnovers': 'Nettoyages en Attente',
    'Turnover Completion': 'Taux de Remise en État',
    'Total Revenue': 'Revenu Total',
    'Room Revenue': 'Revenu Hébergement',
    'Extra Services Revenue': 'Revenu Prestations Annexes',
    'Outstanding Balances': 'Soldes Débiteurs à Recouvrer',
    'Live Front Office': 'Réception en Direct',
    'Room Inventory': 'Inventaire des Chambres',
    'Front Desk Pace': 'Activité Réception',
    'Ready': 'Prêtes',
    'Maint': 'Maintenance',
    'Arrivals': 'Arrivées',
    'Departures': 'Départs',
    'occupied of': 'occupées sur',
    'total rooms': 'chambres au total',
    'Clean & Ready': 'Propres & Prêtes',
    'Turnover Needed': 'Recouche / Ménage Requis',
    'Gross Invoiced': 'Total Facturé',
    'Settled Revenue': 'Chiffre d\'Affaires Encaissé',
    'In-House': 'En Séjour',
    'RECOMMENDED': 'RECOMMANDÉ',
    'CUSTOM': 'PERSONNALISÉ',
    'Manual Setup': 'Configuration Manuelle',
    'Monthly Revenue': 'Chiffre d\'Affaires Mensuel',
    'Departures:': 'Départs :',
    'In-House:': 'En Séjour :',
    'Live Occupancy Pace': 'Rythme d\'Occupation en Direct',
    'Current Occupancy': 'Occupation Actuelle',
    'Available & Inspected': 'Disponibles & Inspectées',
    'Occupied (In-House)': 'Occupées (En Séjour)',
    'Housekeeping / Turnover': 'Gouvernance / Recouche',
    'Maintenance / Out of Order': 'Maintenance / Hors Service',
    'Audit & Billing Summary': 'Synthèse d\'Audit & Facturation',
    'Compliant': 'Conforme',
    'Unsettled Folios': 'Folios Non Soldés',
    'Revenue Per Room (RevPAR)': 'Revenu par Chambre (RevPAR)',
    'View Detailed Audit Reports': 'Consulter les Rapports d\'Audit',
    'In-House & Scheduled Departures': 'Clients en Séjour & Départs Prévus',
    'Today\'s Expected Arrivals': 'Arrivées Prévues Aujourd\'hui',
    'View All Bookings': 'Voir Toutes les Réservations',
    'View All Folios': 'Voir Tous les Folios',
    'No pending arrivals for today.': 'Aucune arrivée en attente pour aujourd\'hui.',
    'No in-house guests currently checked in.': 'Aucun client en séjour actuellement.',
    'Checked In:': 'Arrivé le :',
    'No Folio': 'Sans Folio',
    'Fully Paid': 'Intégralement Payé',
    'Owes': 'Reste Dû',
    'Stay': 'Séjour',
    'night(s)': 'nuitée(s)',
    'Check In': 'Enregistrer l\'Arrivée',
    'Check Out': 'Enregistrer le Départ',
    'All Room Types': 'Tous les Types de Chambres',

    // ── Room & Cleanliness Statuses ──
    'available': 'Disponible',
    'occupied': 'Occupée',
    'reserved': 'Réservée',
    'cleaning': 'En Nettoyage',
    'maintenance': 'En Maintenance',
    'out_of_order': 'Hors Service',
    'Available': 'Disponible',
    'Occupied': 'Occupée',
    'Reserved': 'Réservée',
    'Cleaning': 'En Nettoyage',
    'Maintenance': 'En Maintenance',
    'clean': 'Propre',
    'dirty': 'À Nettoyer',
    'inspected': 'Inspectée',
    'Clean': 'Propre',
    'Dirty': 'À Nettoyer',
    'Inspected': 'Inspectée',
    'Ready for Check-In': 'Prête pour Arrivée',
    'Needs Turnover': 'Nécessite Recouche/Départ',
    'Inspected & Verified': 'Inspectée & Prête',

    // ── Reservation Statuses ──
    'confirmed': 'Confirmée',
    'checked_in': 'En Séjour (Check-In)',
    'checked_out': 'Départ Effectué (Check-Out)',
    'cancelled': 'Annulée',
    'no_show': 'Non-Présentation (No-Show)',
    'Confirmed': 'Confirmée',
    'Checked In': 'En Séjour (Check-In)',
    'Checked Out': 'Départ Effectué (Check-Out)',
    'Cancelled': 'Annulée',
    'No Show': 'Non-Présentation',

    // ── Folio & Billing Terminology ──
    'Folio Status': 'Statut de la Facture',
    'Folio #': 'N° de Folio',
    'Folio ID': 'Identifiant Folio',
    'open': 'Ouvert (En Cours)',
    'settled': 'Soldé (Payé)',
    'void': 'Annulé',
    'Open': 'Ouvert (En Cours)',
    'Settled': 'Soldé',
    'Void': 'Annulé',
    'Room Charges': 'Frais d\'Hébergement',
    'Extra Charges': 'Consommations & Prestations',
    'Total Charges': 'Total des Prestations',
    'Total Paid': 'Total Encaissé',
    'Balance Due': 'Reste à Payer (Solde Dû)',
    'Zero Balance': 'Solde Nul (Tout Réglé)',
    'Overpaid': 'Trop-Perçu',
    'Charge Description': 'Libellé de la Prestation',
    'Category': 'Catégorie',
    'Nightly Room Charge': 'Nuitée d\'Hôtel',
    'Restaurant & Bar': 'Restaurant & Bar',
    'Laundry & Dry Cleaning': 'Blanchisserie & Nettoyage',
    'Minibar': 'Minibar',
    'Room Service': 'Room Service',
    'Spa & Wellness': 'Spa & Soins',
    'Airport Shuttle / Transfer': 'Transfert / Navette Aéroport',
    'Telephone / Business Center': 'Téléphone & Centre d\'Affaires',
    'Damage / Penalty': 'Dégradations & Pénalités',
    'Late Check-Out Fee': 'Frais de Départ Tardif',
    'Early Check-In Fee': 'Frais d\'Arrivée Anticipée',
    'Miscellaneous Charge': 'Diverses Prestations',
    'Payment Method': 'Mode de Règlement',
    'Cash': 'Espèces',
    'Credit Card': 'Carte Bancaire',
    'Debit Card': 'Carte de Débit',
    'Mobile Money': 'Mobile Money',
    'Bank Transfer': 'Virement Bancaire',
    'City Ledger / Company Account': 'Prise en Charge Entreprise (Crédit)',
    'Voucher / Complimentary': 'Bon / Gratuité',
    'Reference / Notes': 'Référence / Notes',
    'Transaction Date': 'Date de Transaction',
    'Posted By': 'Enregistré Par',
    'No charges on this folio yet.': 'Aucune prestation sur ce folio pour l\'instant.',
    'No payments recorded yet.': 'Aucun règlement enregistré pour l\'instant.',

    // ── Front Desk & Check-In / Walk-In Form ──
    'Guest Information': 'Coordonnées du Client',
    'Full Name': 'Nom Complet',
    'Guest Full Name': 'Nom Complet du Client',
    'Email Address': 'Adresse Email',
    'Phone Number': 'Numéro de Téléphone',
    'ID / Passport Number': 'N° Passeport / Pièce d\'Identité',
    'Nationality': 'Nationalité',
    'Special Requests': 'Demandes Particulières',
    'VIP Status': 'Statut VIP',
    'Standard Guest': 'Client Standard',
    'VIP Guest': 'Client VIP',
    'Corporate / Government': 'Société / Délégation Officielle',
    'Room & Dates': 'Chambre & Dates de Séjour',
    'Select Room': 'Sélectionner une Chambre',
    'Assigned Room': 'Chambre Attribuée',
    'Rate Plan': 'Plan Tarifaire',
    'Standard Nightly Rate': 'Tarif Journalier Standard',
    'Custom Agreed Rate': 'Tarif Spécial Négocié',
    'Check-In Date': 'Date d\'Arrivée',
    'Check-Out Date': 'Date de Départ',
    'Duration of Stay': 'Durée du Séjour',
    'Number of Adults': 'Nombre d\'Adultes',
    'Number of Children': 'Nombre d\'Enfants',
    'Deposit / Initial Payment': 'Acompte / Paiement Initial',
    'Deposit Amount': 'Montant de l\'Acompte',
    'Key Card Issued': 'Carte Clé Remise',
    'Confirm Check-In': 'Confirmer l\'Arrivée (Check-In)',
    'Confirm Walk-In Check-In': 'Confirmer l\'Arrivée Sans Réservation',
    'Create Booking': 'Créer la Réservation',
    'Confirm Booking': 'Valider la Réservation',
    'Reservation Code': 'Numéro de Confirmation',

    // ── Room Rack & Floor Matrix ──
    'Filter:': 'Filtrer :',
    'All': 'Tous',
    'All Floors': 'Tous les Étages',
    'Floor': 'Étage',
    'Floor 1': '1er Étage',
    'Floor 2': '2ème Étage',
    'Floor 3': '3ème Étage',
    'Floor 4': '4ème Étage',
    'Floor 5': '5ème Étage',
    'Select Floor': 'Sélectionner un Étage',
    'Floor Number': 'Numéro d\'Étage',
    'Room': 'Chambre',
    'Room Details': 'Détails de la Chambre',
    'Room Category': 'Catégorie de Chambre',
    'Nightly Base Rate': 'Tarif de Base / Nuit',
    'Max Guests': 'Capacité Max',
    'Amenities': 'Équipements & Confort',
    'WiFi': 'Wi-Fi Haut Débit',
    'Air Conditioning (AC)': 'Climatisation',
    'Smart TV / Cable': 'TV Connectée & Câble',
    'En-suite Bathroom': 'Salle de Bain Privative',
    'Balcony / City View': 'Balcon / Vue Ville',
    'Work Desk': 'Bureau de Travail',
    'Mini Fridge': 'Mini Réfrigérateur',
    'Coffee / Tea Maker': 'Cafetière / Bouilloire',
    'Safe Box': 'Coffre-fort Sécurisé',
    'Room Notes': 'Remarques sur la Chambre',
    'Current Occupant': 'Occupant Actuel',
    'No Guest Assigned': 'Aucun client attribué',
    'Expected Check-Out': 'Départ Prévu',

    // ── Bulk Room Generator ──
    'Configure your hotel inventory to start checking in guests, tracking live room turnover, and issuing guest folios.':
      'Configurez votre inventaire hôtelier pour accueillir des clients, suivre l\'état des chambres et émettre des factures folios.',
    'Fast Floor Setup': 'Configuration Rapide d\'Étage',
    'Step-by-Step Configuration': 'Configuration Étape par Étape',
    'Step 1: Create Room Category': 'Étape 1 : Créer une Catégorie de Chambre',
    'Step 2: Add Individual Room': 'Étape 2 : Ajouter une Chambre Individuelle',
    'Automatically generate your room numbers for an entire floor in seconds by selecting a room type and number range (e.g. 101 to 120).':
      'Générez automatiquement les numéros de chambre pour un étage complet en quelques secondes (ex: de 101 à 120).',
    'Define your room categories and nightly rate plans, then add individual rooms according to your exact physical layout.':
      'Définissez vos types de chambres et tarifs, puis enregistrez vos chambres selon la disposition physique de l\'établissement.',
    'Room Number Range': 'Plage de Numéros de Chambres',
    'Start Room Number': 'Numéro de Départ',
    'End Room Number': 'Numéro de Fin',
    'Comma-Separated Room Numbers': 'Numéros séparés par des virgules (ex: 101, 102, 105)',
    'Generated Rooms Preview': 'Aperçu des Chambres Créées',
    'Rooms to Create': 'Chambres à Générer',
    'Total Rooms to Create': 'Total de Chambres à Créer',
    'Generate & Save Rooms': 'Générer & Enregistrer les Chambres',
    'Skip existing rooms': 'Ignorer les numéros déjà existants',

    // ── Housekeeping ──
    'Housekeeping Board': 'Tableau de Gouvernance & Ménage',
    'Room Turnover Status': 'État des Recouches & Nettoyages',
    'Task': 'Tâche',
    'Task Type': 'Type d\'Intervention',
    'Full Departure Turnover': 'Grand Nettoyage (Départ Client)',
    'Daily Stayover Cleaning': 'Recouche Journalière (Client en Séjour)',
    'Deep Maintenance Cleaning': 'Nettoyage Approfondi / Désinfection',
    'Pre-Arrival Inspection': 'Inspection Pré-Arrivée',
    'Housekeeper': 'Gouvernante / Valet',
    'Assignee': 'Affecté À',
    'Unassigned': 'Non Assigné',
    'Priority': 'Priorité',
    'Normal Priority': 'Normale',
    'High Priority': 'Prioritaire (Arrivée Imminente)',
    'Urgent Priority': 'Urgente (Client en Attente)',
    'pending': 'En Attente',
    'in_progress': 'En Cours',
    'completed': 'Terminée',
    'Pending': 'En Attente',
    'In Progress': 'En Cours',
    'Completed': 'Terminée',
    'Start Cleaning': 'Démarrer le Ménage',
    'Finish Cleaning': 'Terminer le Ménage',
    'Pass Inspection': 'Valider l\'Inspection',
    'Housekeeping Notes': 'Observations Ménage',

    // ── Night Audit & Reports ──
    'Daily Hospitality Audit': 'Audit & Clôture Journalière',
    'Audit Window:': 'Période d\'Audit :',
    'Today': 'Aujourd\'hui',
    '7 Days': '7 Derniers Jours',
    '30 Days': '30 Derniers Jours',
    'Last 30 Days': '30 Derniers Jours',
    'Audit Date': 'Date d\'Audit',
    'Room Charges Posted': 'Nuitées Facturées',
    'Total Revenue Audited': 'Chiffre d\'Affaires Audité',
    'In-House Rooms at Audit': 'Chambres Occupées à l\'Audit',
    'Audit Status': 'Statut d\'Audit',
    'Successful': 'Succès',
    'Audit Run By': 'Exécuté Par',
    'Audit Summary': 'Synthèse de l\'Audit',
    'Audit Completed Successfully': 'Audit de Nuit Clôturé avec Succès',
    'Post Nightly Room Charges': 'Facturation Automatique des Nuitées',
    'Verify Room Statuses': 'Vérification de la Cohérence des Chambres',
    'Lock Daily Balances': 'Verrouillage des Soldes Journaliers',
    'Night audit posts nightly room tariffs for all checked-in reservations and computes daily RevPAR.':
      'L\'audit de nuit enregistre les tarifs des nuitées pour tous les clients en séjour et recalcule le RevPAR.',
    'Are you sure you want to run the Night Audit for': 'Confirmez-vous le lancement de l\'audit de nuit pour le',
    'Confirm Night Audit': 'Confirmer la Clôture de l\'Audit',

    // ── 14-Day Visual Tape Chart ──
    '14-Day Tape Chart (Timeline)': 'Planning Visuel sur 14 Jours (Tape Chart)',
    'Booking Table Directory': 'Annuaire & Liste des Réservations',
    'Timeline View': 'Vue Chronologique',
    'Bookings': 'Réservations',
    'Rooms': 'Chambres',
    'Confirmed Bookings': 'Réservations Confirmées',
    'Click an empty cell to book a stay, or click an active reservation bar to view guest details and folio.':
      'Cliquez sur une case libre pour réserver, ou sur une barre de réservation pour consulter le folio.',

    // ── Guest Directory & CRM ──
    'Guest History': 'Historique des Séjours',
    'Total Stays': 'Nombre de Séjours',
    'Total Nights Stayed': 'Total des Nuitées',
    'Total Spent': 'Dépenses Totales',
    'Preferred Room Type': 'Type de Chambre Préféré',
    'Add New Guest': 'Ajouter un Nouveau Client',
    'Edit Guest Profile': 'Modifier la Fiche Client',
    'Search guest by name, phone, email, or passport...': 'Rechercher un client par nom, téléphone, email ou passeport...',

    // ── Modals, Notifications & Alerts ──
    'Cancel': 'Annuler',
    'Save Changes': 'Enregistrer les Modifications',
    'Create Room': 'Créer la Chambre',
    'Update Room': 'Mettre à Jour la Chambre',
    'Delete Room': 'Supprimer la Chambre',
    'Success': 'Succès',
    'Error': 'Erreur',
    'Loading hotel data...': 'Chargement des données de l\'hôtel...',
    'Hotel property data refreshed.': 'Données de l\'hôtel actualisées avec succès.',
    'Reservation created successfully!': 'Réservation créée avec succès !',
    'Guest checked in successfully!': 'Arrivée du client (Check-In) enregistrée avec succès !',
    'Guest checked out successfully!': 'Départ du client (Check-Out) enregistré avec succès !',
    'Payment posted successfully!': 'Paiement enregistré avec succès sur le folio !',
    'Extra charge added to folio!': 'Prestation ajoutée au folio avec succès !',
    'Night audit completed successfully!': 'Audit de nuit exécuté et clôturé avec succès !',
    'Housekeeping task updated!': 'Tâche d\'entretien mise à jour avec succès !',
    'Room added successfully!': 'Chambre ajoutée avec succès !',
    'Bulk rooms created successfully!': 'Chambres créées en lot avec succès !',
    'Room category saved successfully!': 'Catégorie de chambre enregistrée avec succès !',
    'Confirm Check-Out': 'Confirmer le Départ',
    'Are you sure you want to check out this guest? Balance must be zero or settled.':
      'Êtes-vous sûr de vouloir enregistrer le départ de ce client ? Le solde doit être nul ou réglé.',
    'Outstanding balance detected!': 'Attention : Solde restant dû non soldé !',

    // ── Additional Professional PMS French Terminology ──
    'Days': 'Jours',
    'Previous Period': 'Période Précédente',
    'Next Period': 'Période Suivante',
    'Rooms Visible': 'Chambres Affichées',
    'of': 'sur',
    'No Rooms Available on Tape Chart': 'Aucune Chambre sur le Planning Visuel',
    'Your property currently has 0 rooms configured. Populate starter demo inventory or configure rooms manually to begin managing reservations.':
      'Votre établissement compte actuellement 0 chambre. Générez un inventaire de démonstration ou créez vos chambres manuellement pour débuter.',
    'No rooms match your current floor or room category filters. Clear filters to view full property timeline.':
      'Aucune chambre ne correspond à vos filtres d\'étage ou de catégorie. Réinitialisez les filtres pour afficher l\'ensemble des chambres.',
    'Seed Starter Rooms': 'Générer des Chambres de Démonstration',
    'Seed Starter Rooms & Inventory': 'Générer l\'Inventaire de Démonstration',
    'Room & Category': 'Chambre & Catégorie',
    'TODAY': 'AUJOURD\'HUI',
    'Dates': 'Dates',
    'Duration': 'Durée',
    'Rate': 'Tarif',
    'Status': 'Statut',
    'Folio': 'Folio',
    'Book': 'Réserver',
    'Fl': 'Ét.',
    'Standard': 'Standard',
    'Standard Room': 'Chambre Standard',
    'Cleanliness': 'Propreté',
    'No contact provided': 'Aucun contact renseigné',
    'Action': 'Action',
    'Actions': 'Actions',
    'Guest': 'Client',
    'Guest Name': 'Nom du Client',
    'Room / Type': 'Chambre / Catégorie',
    'No phone': 'Sans téléphone',
    'night': 'nuit',
    'nights': 'nuits',
    'Bedding:': 'Literie :',
    'Max Occupancy:': 'Capacité Maximale :',
    'Adults': 'Adultes',
    'Amenities:': 'Équipements :',
    'Edit': 'Modifier',
    'Delete': 'Supprimer',
    'Edit Room Details': 'Modifier la Chambre',
    'Are you sure you want to delete this room?': 'Êtes-vous sûr de vouloir supprimer cette chambre ?',
    'Yes, Delete': 'Oui, Supprimer',
    'Cannot delete occupied room': 'Impossible de supprimer une chambre occupée',
    'Book In Advance': 'Réserver à l\'Avance',
    'Folio & Charges': 'Folio & Facturation',
    'Mark Inspected & Ready': 'Marquer Inspectée & Prête',
    'Clear Maintenance': 'Lever la Maintenance',
    'Needs Cleaning': 'Nettoyage Requis',
    'Inspect': 'Inspecter',
    '✓ Inspected': '✓ Inspectée',
    'Turnover': 'Recouche',
    'Start Clean': 'Démarrer le Ménage',
    'Dispatch Cleaning Task': 'Assigner une Tâche de Ménage',
    'Dispatch New Cleaning Task': 'Assigner une Nouvelle Tâche',
    'Create Cleaning Task': 'Créer une Tâche de Ménage',
    'All Turnover Tickets Resolved': 'Toutes les Tâches de Remise en État Sont Résolues',
    'There are currently no active housekeeping or maintenance tickets matching your filters. All rooms are either serviced or inspected.':
      'Il n\'y a actuellement aucun ticket d\'entretien ou de ménage en attente. Toutes les chambres sont entretenues ou inspectées.',
    'Inventory': 'Inventaire',
    'In Cleaning': 'Ménage en Cours',
    'Open Tasks': 'Tâches Ouvertes',
    'Awaiting service': 'En attente d\'intervention',
    'All clear': 'Toutes prêtes',
    'Attendant active': 'Gouvernante en cours',
    'None in progress': 'Aucun en cours',
    'Out of order': 'Hors service',
    'Zero defects': 'Aucune anomalie',
    'logged': 'enregistrées',
    'Turnover Matrix': 'Matrice de Gouvernance',
    'Task Audit List': 'Registre des Tâches',
    'All Priorities': 'Toutes les Priorités',
    'Urgent': 'Urgente',
    'High': 'Élevée',
    'Normal': 'Normale',
    'Low': 'Faible',
    'Search room, attendant, note...': 'Rechercher chambre, gouvernante, note...',
    'No Rooms Configured in Hotel Property': 'Aucune Chambre Configurée dans l\'Établissement',
    'Add guest rooms or seed starter inventory to begin tracking real-time room cleanliness, turnovers, and attendant assignments.':
      'Ajoutez des chambres ou générez l\'inventaire de démonstration pour suivre la propreté, les recouches et les gouvernantes.',
    'No rooms match the selected filters or search query.': 'Aucune chambre ne correspond aux critères sélectionnés.',
    'Reset Filters': 'Réinitialiser les Filtres',
    'All Statuses': 'Tous les Statuts',
    'Search guest, reservation code, phone...': 'Rechercher client, code, téléphone...',
    'No guest reservations found': 'Aucune réservation trouvée',
    'Code': 'Code',
    'Room Assigned': 'Chambre Attribuée',
    'Dates / Nights': 'Dates / Nuitées',
    'Financials': 'Facturation',
    'Adult(s)': 'Adulte(s)',
    'Paid:': 'Payé :',
    'Due:': 'Reste Dû :',
    'Cancel this reservation?': 'Annuler cette réservation ?',
    'Yes, Cancel': 'Oui, Annuler',
    'No': 'Non',
    'Ledger Table': 'Grand Livre (Tableau)',
    'Folio Cards': 'Cartes des Folios',
    'All Balances': 'Tous les Soldes',
    'Balance Due Only': 'Soldes Débiteurs Uniquement',
    'Fully Settled ($0)': 'Intégralement Soldés ($0)',
    'Search guest, folio #, room...': 'Rechercher client, n° folio, chambre...',
    'No Guest Billing Folios Found': 'Aucun Folio de Facturation Trouvé',
    'Guest folios are automatically opened whenever a guest books a room or checks in. Folios track room nights, POS dining charges, minibar items, and settlements.':
      'Les folios sont automatiquement ouverts lors d\'une réservation ou d\'une arrivée. Ils enregistrent les nuitées, consommations bar/restaurant, minibar et encaissements.',
    'Create New Booking': 'Créer une Nouvelle Réservation',
    'No folios match the selected filters or search query.': 'Aucun folio ne correspond aux critères sélectionnés.',
    'Subtotal': 'Sous-Total',
    'Tax (VAT)': 'Taxe (TVA)',
    'Total Bill': 'Montant Total',
    'Paid Amount': 'Montant Réglé',
    'Manage Folio': 'Gérer le Folio',
    'Manage Folio & Billing': 'Gérer le Folio & la Facturation',
    'Total Invoiced:': 'Total Facturé :',
    'Amount Settled:': 'Montant Encaissé :',
    'Balance Due:': 'Reste à Payer :',
    'Account Balance:': 'Solde du Compte :',
    'Settled Folios': 'Folios Soldés',
    'Closed Accounts': 'Comptes Clôturés',
    'Archived': 'Archivé',
    'Zero debt': 'Solde nul',
    'Active in-house': 'Clients en séjour',
    'Reconciled': 'Réconcilié',
    'Property Room Categories & Rates': 'Catégories de Chambres & Tarifs de l\'Établissement',
    'Configure room categories, nightly rates, bed arrangements, and amenities.':
      'Configurez les catégories de chambres, tarifs par nuitée, literie et équipements.',
    'Add Room Category': 'Ajouter une Catégorie',
    'No room types created yet. Click \'+ Add Room Type\' to set up categories.':
      'Aucun type de chambre créé. Cliquez sur "+ Ajouter une Catégorie" pour configurer.',
    'No description provided.': 'Aucune description fournie.',
    'Delete this room type?': 'Supprimer cette catégorie de chambre ?',
    'Print Audit': 'Imprimer l\'Audit',
    'Gross Revenue': 'Revenu Brut Hébergement',
    'Bookings Invoiced': 'Réservations Facturées',
    '✓ Reconciled Ledger': '✓ Grand Livre Réconcilié',
    'Outstanding Ledger': 'Grand Livre Débiteur',
    'Open Guest Folios': 'Folios Clients Ouverts',
    'Room Nights Sold': 'Nuitées Vendues',
    'Across Period Window': 'Sur la Période',
    'ADR (Avg Daily Rate)': 'Prix Moyen Chambre (ADR)',
    'Yield per Sold Room': 'Rendement par Chambre Vendue',
    'RevPAR / Room': 'RevPAR / Chambre',
    'Occupancy': 'Occupation',
    'Departmental Revenue Streams': 'Revenus par Département',
    'Room Accommodation': 'Hébergement & Nuitées',
    'Restaurant & Bar F&B': 'Restaurant & Bar (F&B)',
    'Laundry Services': 'Blanchisserie',
    'Amenities & Minibar': 'Équipements & Minibar',
    'Miscellaneous & Other': 'Prestations Diverses',
    'of total revenue': 'du chiffre d\'affaires total',
    'Operations & Housekeeping Performance': 'Performance Opérationnelle & Gouvernance',
    'Turnover Completed': 'Remises en État Achevées',
    'Cleaned Rooms': 'Chambres Nettoyées',
    'Pending Turnover': 'Recouches en Attente',
    'Awaiting Staff': 'En Attente de Personnel',
    'Inspected & Approved': 'Inspectées & Validées',
    'Supervisor Verified': 'Vérifiées par Gouvernante',
    'Total Service Volume': 'Volume Total d\'Interventions',
    'Overall Dispatch Tasks': 'Total des Tâches Assignées',
    'Night Audit & Ledger Status': 'Audit de Nuit & Clôture Journalière',
    'Automatically posts room rate charges and rolls forward billing cycles':
      'Enregistre automatiquement les nuitées et fait basculer la date d\'exploitation',
    'Run Audit': 'Lancer l\'Audit',
    '7-Day Occupancy & Revenue Trend': 'Tendance d\'Occupation & Revenus sur 7 Jours',
    'Date': 'Date',
    'Occupied / Total Rooms': 'Chambres Occupées / Total',
    'Daily Revenue': 'Revenu Journalier',
    'Room Type Yield & Occupancy Performance': 'Rendement & Performance par Type de Chambre',
    'Room Type Category': 'Catégorie de Chambre',
    'Available Inventory': 'Inventaire Disponible',
    'Nights Booked': 'Nuitées Réservées',
    'Guest Profiles': 'Fiches Clients',
    'Registered accounts': 'Comptes enregistrés',
    'VIP Members': 'Membres VIP',
    'Diamond & Gold tiers': 'Niveaux Diamant & Or',
    'Accumulated visits': 'Visites cumulées',
    'Lifetime Spend': 'Dépenses Totales Cumulées',
    'Gross billing value': 'Valeur brute facturée',
    'Repeat Rate': 'Taux de Fidélité',
    'Guests with >1 stay': 'Clients avec >1 séjour',
    'Identified': 'Identités Vérifiées',
    'Passport / National ID': 'Passeport / Pièce d\'Identité',
    'Search by Guest Name, Passport, Phone, Email...': 'Rechercher par nom, passeport, téléphone, email...',
    'All VIP Tiers': 'Tous les Niveaux VIP',
    'VIP Diamond': 'VIP Diamant',
    'Gold Tier': 'Niveau Or',
    'Silver Tier': 'Niveau Argent',
    'Standard Member': 'Membre Standard',
    'Register New Guest': 'Créer une Fiche Client',
    'Register First Guest': 'Créer la Première Fiche',
    'Loyalty & VIP Tier': 'Fidélité & Statut VIP',
    'ID / Passport & Nationality': 'N° Pièce d\'Identité & Nationalité',
    'Special Preferences / Notes': 'Préférences & Observations',
    'Register Guest Profile': 'Enregistrer la Fiche Client',
    'No Guest Profiles Match Your Filter': 'Aucune Fiche Client ne Correspond au Filtre',
    'No Guest Profiles Registered Yet': 'Aucune Fiche Client Enregistrée pour le Moment',
    'No guest accounts match your current query or filter. Try clearing your search parameters.':
      'Aucun compte client ne correspond à votre recherche. Réinitialisez vos critères de filtre.',
    'Guest relationship dossiers are automatically created upon reservation or check-in, or you can register VIP clients in advance.':
      'Les fiches clients sont créées automatiquement lors d\'une réservation ou d\'une arrivée, ou enregistrées par avance.',
    'Not specified': 'Non renseigné',
    'Nationality not set': 'Nationalité non définie',
    'None': 'Aucune',
    'Create New Reservation': 'Créer une Nouvelle Réservation',
    'Contact Phone': 'Téléphone de Contact',
    'National ID / Passport Number': 'N° Passeport / Pièce d\'Identité',
    'Assign Room': 'Attribuer une Chambre',
    'Select room': 'Sélectionner une chambre',
    'Check-In & Check-Out Dates': 'Dates d\'Arrivée & de Départ',
    'Booking Channel': 'Canal de Réservation',
    'Direct Hotel Walk-In': 'Arrivée Directe à l\'Hôtel',
    'Phone / WhatsApp': 'Téléphone / WhatsApp',
    'Advance Deposit': 'Acompte Anticipé',
    'Special Requests / Notes': 'Demandes Particulières / Remarques',
    'Estimated Stay:': 'Séjour Estimé :',
    'Estimated Bill (incl. 18% VAT):': 'Facturation Estimée (dont 18% TVA) :',
    'Confirm & Save Booking': 'Confirmer & Enregistrer la Réservation',
    'Express Walk-In Guest Check-In': 'Arrivée Directe Sans Réservation (Walk-In Express)',
    'Guest Mobile Phone': 'Téléphone Portable du Client',
    'National ID / Passport #': 'N° Passeport / Pièce d\'Identité',
    'Assign Available Room': 'Attribuer une Chambre Disponible',
    'Stay Period': 'Période du Séjour',
    'Payment / Deposit Received': 'Paiement / Acompte Reçu',
    'Check-In & Issue Room Key': 'Valider l\'Arrivée & Remettre la Clé',
    'Check-In Guest —': 'Enregistrement d\'Arrivée —',
    'Confirm / Switch Assigned Room': 'Confirmer / Changer la Chambre Attribuée',
    'Deposit at Check-In': 'Acompte à l\'Arrivée',
    'Confirm Check-In & Issue Key': 'Confirmer l\'Arrivée & Délivrer la Clé',
    'Check-Out & Settlement —': 'Départ & Clôture de Facture —',
    'Total Charges:': 'Total des Prestations :',
    'Payments Received:': 'Paiements Encaissés :',
    'Balance Due at Check-Out:': 'Solde Dû au Départ :',
    'Settlement Payment Method': 'Mode de Règlement du Solde',
    'Check-Out Remarks': 'Observations au Départ',
    'Settle Folio & Complete Check-Out': 'Régler la Facture & Valider le Départ',
    'Folio Invoice —': 'Facture Folio Client —',
    'Close': 'Fermer',
    'Post Payment': 'Enregistrer un Paiement',
    'Post Room Charge': 'Ajouter une Prestation',
    'Itemized Room Charges & Payments': 'Détail des Prestations & Règlements',
    'Type': 'Type',
    'Description': 'Libellé',
    'Qty': 'Qté',
    'Unit Price': 'Prix Unitaire',
    'Total': 'Total',
    'Total Payments:': 'Total Règlements :',
    'Post Charge to Guest Folio': 'Ajouter une Prestation au Folio',
    'Charge Category': 'Catégorie de Prestation',
    'Restaurant Dining / Room Service': 'Restauration / Room Service',
    'In-Room Minibar Consumable': 'Consommations Minibar',
    'Spa & Wellness Treatment': 'Soins Spa & Bien-Être',
    'Airport Shuttle / Extra Amenity': 'Navette Aéroport / Prestation Extra',
    'Property Damage / Loss': 'Dégradation / Perte de Matériel',
    'Item Description': 'Description de la Prestation',
    'Quantity': 'Quantité',
    'Add Charge to Bill': 'Ajouter la Prestation à la Facture',
    'Record Guest Payment to Folio': 'Enregistrer un Encaissement sur le Folio',
    'Payment Amount': 'Montant du Règlement',
    'Reference / Transaction ID': 'Référence / N° de Transaction',
    'Add Hotel Room': 'Ajouter une Chambre',
    'Room Number': 'Numéro de Chambre',
    'Floor / Level': 'Étage / Niveau',
    'Room Category / Type': 'Catégorie de Chambre',
    'Notes / Features': 'Remarques & Caractéristiques',
    'Save Room': 'Enregistrer la Chambre',
    'Bulk Floor & Room Generator': 'Générateur Groupé d\'Étage & Chambres',
    'Rapidly create rooms by floor sequence or custom list': 'Créez rapidement des chambres par séquence ou liste personnalisée',
    'Numbering Mode': 'Mode de Numérotation',
    'Sequential Range (e.g. 101 to 120)': 'Plage Séquentielle (ex: de 101 à 120)',
    'Custom Comma-Separated List': 'Liste Personnalisée Séparée par Virgules',
    'Prefix (Optional)': 'Préfixe (Optionnel)',
    'Start Number': 'Numéro de Début',
    'End Number': 'Numéro de Fin',
    'Room Numbers (separated by comma or new lines)': 'Numéros de Chambres (séparés par virgules ou retours à la ligne)',
    'Default Room Notes / Features (Optional)': 'Caractéristiques par Défaut (Optionnel)',
    'Smart Inventory Protection:': 'Protection Intelligente de l\'Inventaire :',
    'Any room numbers that already exist in your property will be automatically skipped to prevent accidental overwriting or duplicates.':
      'Tous les numéros de chambre déjà existants seront automatiquement ignorés pour éviter les doublons.',
    'Generate Rooms': 'Générer les Chambres',
    'Edit Room #': 'Modifier la Chambre #',
    'Create Room Type & Pricing': 'Créer une Catégorie de Chambre & Tarification',
    'Edit Room Type —': 'Modifier la Catégorie —',
    'Room Type Name': 'Nom de la Catégorie',
    'Base Nightly Rate': 'Tarif de Base par Nuitée',
    'Max Occupancy (Guests)': 'Capacité Maximale (Personnes)',
    'Bed Arrangement': 'Configuration de la Literie',
    'Save Room Type': 'Enregistrer la Catégorie',
    'Create Housekeeping / Maintenance Task': 'Créer une Tâche de Ménage / Maintenance',
    'Task Category': 'Type d\'Intervention',
    'Turnover Post Check-Out': 'Grand Ménage Après Départ',
    'Stayover Daily Cleaning': 'Recouche Quotidienne (Client en Séjour)',
    'Deep Sanitization': 'Désinfection Approfondie',
    'Manager Inspection': 'Inspection de la Gouvernante Générale',
    'Maintenance Fix': 'Intervention Technique & Réparation',
    'Priority Level': 'Niveau de Priorité',
    'Assign To Staff Member': 'Assigner à un Membre du Personnel',
    'Instructions / Notes': 'Consignes & Remarques',
    'Night Audit & Daily Balance Reconciliation': 'Audit de Nuit & Clôture Journalière des Soldes',
    'Close Reconciliation Summary': 'Fermer le Rapport de Clôture',
    'Night Audit Process Completed Successfully': 'Processus d\'Audit de Nuit Terminé avec Succès',
    'Rooms In-House Charged': 'Chambres Occupées Facturées',
    'Night Room Revenue Posted': 'Revenu Nuitées Enregistré',
    'Current Guest Ledger': 'Solde Actuel du Grand Livre',
    'Vacant Rooms': 'Chambres Vacantes',
    'Credit Card (POS Terminal)': 'Carte Bancaire (Terminal TPE)',
    'Credit / Debit Card': 'Carte Bancaire / Débit',
    'Credit / Debit Card POS': 'Carte Bancaire (TPE)',
    'Direct Bank Transfer': 'Virement Bancaire Direct',
    'Loyalty Tier / VIP Status': 'Niveau de Fidélité / Statut VIP',
    'Silver Member': 'Membre Argent',
    'Gold Member': 'Membre Or',
    'Guest Preferences & VIP Notes': 'Préférences du Client & Notes VIP',
    'Table': 'Tableau',
    'Cards': 'Cartes',
    'Stays': 'Séjours',
    'Spend': 'Dépenses',
    'Hotel Inventory Not Configured Yet': 'Inventaire Hôtelier Non Configuré',
    'Max': 'Max',
    'Guests': 'Personnes',
    'No contact specified': 'Aucun contact renseigné',
    'Cleaning Active': 'Ménage en Cours',
    'Pending Tickets': 'Tickets en Attente',
    'Assigned Attendant': 'Gouvernante Assignée',
    'Delete this task?': 'Supprimer cette tâche ?',
    'Yes': 'Oui',
    '👑 VIP Diamond': '👑 VIP Diamant',
    '🥇 Gold Tier': '🥇 Niveau Or',
    '🥈 Silver Tier': '🥈 Niveau Argent',
    'OTA (Booking.com, Expedia)': 'Agences OTA (Booking, Expedia)',
    'Subtotal:': 'Sous-Total :',
    'Tax (18% VAT):': 'Taxe (TVA 18%) :',
    'floors': 'étages',
    'floor': 'étage',
    'ready': 'prêtes',
    'Cleaned': 'Nettoyée',
    'Notes': 'Remarques',
    'No email': 'Sans email',
    'Mark as Needing Turnover': 'Marquer comme Recouche Nécessaire',
    'King Bed': 'Lit King Size',
    'Queen Bed': 'Lit Queen Size',
    'Twin Beds': 'Lits Jumeaux',
    'Single Bed': 'Lit Simple',
    'Double Bed': 'Lit Double',
    'Nights': 'Nuitées',
    'rooms': 'chambres',
    'Register New Guest Profile': 'Enregistrer la Fiche Client',
    'Phone / WhatsApp Number': 'Numéro Téléphone / WhatsApp',
    'Nationality / Country': 'Nationalité / Pays d\'Origine',
    'Please enter guest full name': 'Veuillez saisir le nom complet du client',
    'Reservation Code:': 'N° Réservation :',
    'Room:': 'Chambre :',
    'Stay:': 'Séjour :',
    'Audit Date:': 'Date de l\'Audit :',
    'Status:': 'Statut :',
    'Payments': 'Règlements',
    'No audit summary available': 'Aucun rapport d\'audit disponible',
    'Please select room': 'Veuillez choisir une chambre',
    'Please pick dates': 'Veuillez choisir les dates de séjour',
    'Room number required': 'Le numéro de chambre est obligatoire',
    'Please enter floor': 'Veuillez renseigner l\'étage',
    'Please enter room numbers': 'Veuillez saisir les numéros de chambre',
    'Description required': 'La description est obligatoire',
    'Name required': 'Le nom est obligatoire',
    'Night(s)': 'Nuitée(s)',
    'Night': 'Nuitée',
    'Guest Bill': 'Facture Client',
    'Free High-Speed WiFi': 'Wi-Fi Haut Débit Gratuit',
    'Air Conditioning': 'Climatisation Réversible',
    '55-inch Smart TV': 'Smart TV 4K 55 Pouces',
    'Private Ensuite Bath': 'Salle de Bain Privative Attenante',
    'Minibar & Espresso Machine': 'Minibar & Machine Espresso',
    'Balcony with View': 'Balcon avec Vue Panoramique',
    'Work Desk & Ergonomic Chair': 'Espace Bureau & Fauteuil Ergonomique',
    'Jacuzzi': 'Baignoire Balnéo / Jacuzzi',
    'Type amenity and press enter': 'Saisissez un équipement et appuyez sur Entrée',
    'ID or Passport document number': 'Numéro de passeport ou pièce d\'identité',
    'Verification document number': 'Numéro du document de vérification',
    'Confirm room selection': 'Confirmer la sélection de la chambre',
    'e.g. Minibar verified, key returned in good order': 'ex. Minibar vérifié, clés restituées en bon état',
  },
  rw: {
    'Create New Reservation': 'Gukora Komande Nshya',
    'Confirm & Save Booking': 'Emeza & Bika Komande',
    'Express Walk-In Guest Check-In': 'Kwinjira Ako Kanya (Walk-In Express)',
    'Check-In & Issue Room Key': 'Kwakira Umushyitsi & Kumuha Urufunguzo',
    'Settle Folio & Complete Check-Out': 'Kwishyura & Gusohoka',
    'Post Room Charge': 'Kwandika Ikiguzi ku Cyumba',
    'Record Guest Payment to Folio': 'Kwandika Ubwishyu bw\'Umushyitsi',
    'Add Hotel Room': 'Kongeraho Icyumba',
    'Bulk Floor & Room Generator': 'Gukora Ibyumba Byinshi Rimwe',
    'Save Room': 'Bika Icyumba',
    'Save Room Type': 'Bika Ubwoko bw\'Icyumba',
    'Create Housekeeping / Maintenance Task': 'Gukora Igikorwa cy\'Isuku / Isana',
    'Dispatch Cleaning Task': 'Ohereza Igikorwa cy\'Isuku',
    'Night Audit & Daily Balance Reconciliation': 'Igenzura ry\'Ijoro & Kuringaniza Amafaranga',
    'Register New Guest Profile': 'Kwandika Umushyitsi Mushya',
    'Edit Guest Profile': 'Guhindura Umwirondoro w\'Umushyitsi',
    'Full Name': 'Amazina Yose',
    'Close': 'Funga',
    'Hotel Management': 'Ubuyobozi bwa Hoteli',
    'Hotel Overview & Analytics': 'Incamake ya Hoteli & Imibare',
    'Live Room Rack & Floor Matrix': 'Ikarita y\'Ibyumba & Amagorofa',
    'Front Desk & Reservations': 'Ukwakirira Abashyitsi & Kubitsa',
    'Guest Folios & Billing Suite': 'Inyemezabwishyu z\'Abashyitsi',
    'Housekeeping & Turnover Operations': 'Isuzuma ry\'Isuku & Gutegura Ibyumba',
    'Room Categories & Pricing Plans': 'Ubwoko bw\'Ibyumba & Ibiciro',
    'Financial Audit & Hospitality Reports': 'Ibaruramutungo & Raporo za Hoteli',
    'Guest Directory & CRM': 'Urutonde rw\'Abashyitsi',
    'Refresh': 'Kuvugurura',
    'Night Audit': 'Igenzura ry\'Ijoro',
    'Walk-In Check-In': 'Kwinjira Ako Kanya (Walk-In)',
    'New Booking': 'Kubitsa Icyumba Gishya',
    'Occupancy Rate': 'Igipimo cy\'Ubwinjiriro bw\'Ibyumba',
    'Available': 'Birahari',
    'Occupied': 'Birafashwe',
    'Reserved': 'Byabitswe',
    'Cleaning': 'Biri Gukorwaho Isuku',
    'Maintenance': 'Biri Gusanwa',
    'Clean': 'Gisukuye',
    'Dirty': 'Gikeneye Isuku',
    'Inspected': 'Cyasuzumwe',
    'Confirmed': 'Byemejwe',
    'Checked In': 'Yinjiye',
    'Checked Out': 'Yasohotse',
    'Cancelled': 'Byahagaritswe',
    'All Floors': 'Amagorofa Yose',
    'Floor': 'Igorofa',
    'Save Changes': 'Bika Impinduka',
    'Cancel': 'Reka',
  },
};
